<template>
  <v-dialog
    :value="value"
    persistent
    scrollable
    content-class="cycle-status-confirm-dialog"
    data-testid="cycles-page__status-confirm-dialog"
    @input="onInput"
  >
    <v-card
      class="cycle-status-confirm"
      :class="`cycle-status-confirm--${mode}`"
      :loading="loading"
      :disabled="loading"
      loader-height="2"
      data-testid="cycles-page__status-confirm-body"
    >
      <!-- Header: icon + title/period + close -->
      <div class="cycle-status-confirm__header" data-testid="cycles-page__status-confirm-header">
        <div class="cycle-status-confirm__header-left">
          <div
            class="cycle-status-confirm__icon-wrap"
            :class="`cycle-status-confirm__icon-wrap--${mode}`"
            data-testid="cycles-page__status-confirm-icon"
          >
            <v-icon
              :color="headerIconColor"
              size="22"
            >
              {{ icon }}
            </v-icon>
          </div>
          <div class="cycle-status-confirm__titles">
            <h4
              class="cycle-status-confirm__title"
              data-testid="cycles-page__status-confirm-title"
            >
              {{ titleText }}
            </h4>
            <p
              class="cycle-status-confirm__period"
              data-testid="cycles-page__status-confirm-period"
            >
              {{ periodText }}
            </p>
          </div>
        </div>
        <v-tooltip bottom content-class="tooltip-bottom" open-delay="500">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              small
              class="cycle-status-confirm__close-btn"
              aria-label="Close"
              :disabled="loading"
              data-testid="dialog-box-new__close-btn"
              @click="onCancel"
            >
              <v-icon size="22">mdi-close</v-icon>
            </v-btn>
          </template>
          <span>Close</span>
        </v-tooltip>
      </div>

      <!-- Body: banner + summary -->
      <div class="cycle-status-confirm__body">
        <div
          class="cycle-status-confirm__banner"
          :class="`cycle-status-confirm__banner--${mode}`"
          data-testid="cycles-page__status-confirm-banner"
        >
          <v-icon
            class="cycle-status-confirm__banner-icon"
            :color="bannerIconColor"
            size="22"
          >
            {{ bannerIcon }}
          </v-icon>
          <div class="cycle-status-confirm__banner-copy">
            <p class="cycle-status-confirm__banner-text">{{ bannerText }}</p>
            <p
              v-if="showUpsiWarning"
              class="cycle-status-confirm__banner-text"
              data-testid="cycles-page__status-confirm-upsi-warning"
            >
              {{ upsiWarning }}
            </p>
          </div>
        </div>

        <div
          class="cycle-status-confirm__summary"
          data-testid="cycles-page__status-confirm-summary"
        >
          <div
            v-for="row in summaryRows"
            :key="row.key"
            class="cycle-status-confirm__summary-row"
            :class="{ 'cycle-status-confirm__summary-row--muted': row.muted }"
            :data-testid="`cycles-page__status-confirm-row--${row.key}`"
          >
            <div class="cycle-status-confirm__row-left">
              <span
                class="cycle-status-confirm__dot"
                :class="{ 'cycle-status-confirm__dot--hidden': row.hideDot }"
                :style="row.hideDot ? null : { backgroundColor: row.dotColor }"
              />
              <span class="cycle-status-confirm__row-label">{{ row.label }}</span>
            </div>
            <span
              class="cycle-status-confirm__row-value"
              :style="row.valueColor ? { color: row.valueColor } : null"
            >
              {{ row.value }}
            </span>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="cycle-status-confirm__footer" data-testid="cycles-page__status-confirm-actions">
        <v-btn
          outlined
          class="cycle-status-confirm__cancel-btn text-none"
          :disabled="loading"
          data-testid="cycles-page__status-confirm-cancel-btn"
          @click="onCancel"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          class="cycle-status-confirm__confirm-btn text-none"
          :loading="loading"
          :disabled="loading"
          data-testid="cycles-page__status-confirm-confirm-btn"
          @click="$emit('confirm')"
        >
          {{ resolvedConfirmLabel }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import './CycleStatusConfirmDialog.scss';
import {
  calcCompletion,
  formatCycleMonthYear,
  formatPeriodRange,
  getCloseConfirmLabel,
} from '../../utils/cyclesHelpers';

export default {
  name: 'CycleStatusConfirmDialog',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'close',
      validator: (v) => ['close', 'reopen'].includes(v),
    },
    cycle: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    confirmLabel: {
      type: String,
      default: '',
    },
    upsiWarning: {
      type: String,
      default: '',
    },
  },

  computed: {
    isClose() {
      return this.mode === 'close';
    },

    showUpsiWarning() {
      return this.mode === 'reopen' && String(this.upsiWarning || '').trim() !== '';
    },

    icon() {
      return this.isClose ? 'mdi-alert-outline' : 'mdi-restore';
    },

    bannerIcon() {
      return this.isClose ? 'mdi-alert-outline' : 'mdi-information-outline';
    },

    headerIconColor() {
      return this.isClose ? 'var(--amber-default)' : 'var(--grey-700)';
    },

    bannerIconColor() {
      return this.isClose ? 'var(--amber-default)' : 'var(--workflow-blue-local)';
    },

    monthYear() {
      return formatCycleMonthYear(this.cycle && this.cycle.from);
    },

    titleText() {
      const label = this.monthYear || 'Cycle';
      return this.isClose
        ? `Close The ${label} Cycle?`
        : `Reopen The ${label} Cycle?`;
    },

    periodText() {
      if (!this.cycle) return '';
      return (
        this.cycle.periodText ||
        formatPeriodRange(this.cycle.from, this.cycle.to)
      );
    },

    bannerText() {
      return this.isClose
        ? 'Closing locks every task for this period — no further submissions or edits. Review what\'s still open:'
        : 'Reopening unlocks every task for this period — submissions and edits will be allowed again. Review what was locked:';
    },

    summary() {
      return (this.cycle && this.cycle.summary) || {};
    },

    completionPct() {
      return calcCompletion(this.summary);
    },

    summaryRows() {
      const pending = this.summary.pending || 0;
      const pendingapproval = this.summary.pendingapproval || 0;
      const approved = this.summary.approved || 0;

      // Figma tokens: amber-default, workflow-blue-local, status/workflow green
      const amber = 'var(--amber-default)';
      const blue = 'var(--workflow-blue-local)';
      const green = 'var(--workflow-green)';
      const grey = 'var(--text-grey-sub)';

      if (this.isClose) {
        return [
          {
            key: 'pending',
            label: 'Pending submission',
            value: pending,
            dotColor: grey,
            valueColor: grey,
          },
          {
            key: 'pendingapproval',
            label: 'Pending approval',
            value: pendingapproval,
            dotColor: amber,
            valueColor: amber,
          },
          {
            key: 'approved',
            label: 'Approved',
            value: approved,
            dotColor: green,
            valueColor: green,
          },
          {
            key: 'completion',
            label: 'Completion',
            value: `${this.completionPct}%`,
            hideDot: true,
            muted: true,
            valueColor: 'var(--text-grey-default)',
          },
        ];
      }

      return [
        {
          key: 'pending',
          label: 'Pending submission',
          value: pending,
          dotColor: amber,
          valueColor: amber,
        },
        {
          key: 'pendingapproval',
          label: 'Pending approval',
          value: pendingapproval,
          dotColor: blue,
          valueColor: blue,
        },
        {
          key: 'approved',
          label: 'Approved',
          value: approved,
          dotColor: green,
          valueColor: green,
        },
        {
          key: 'completion',
          label: 'Completion',
          value: `${this.completionPct}%`,
          hideDot: true,
          muted: true,
          valueColor: 'var(--text-grey-default)',
        },
      ];
    },

    resolvedConfirmLabel() {
      if (this.confirmLabel) return this.confirmLabel;
      if (this.isClose) return getCloseConfirmLabel(this.summary);
      return 'Reopen cycle';
    },
  },

  methods: {
    onInput(val) {
      this.$emit('input', val);
      if (!val) this.$emit('cancel');
    },

    onCancel() {
      this.$emit('input', false);
      this.$emit('cancel');
    },
  },
};
</script>
